import { useEffect, useState } from 'react';
import Layout from '../../components/Layout/Layout';
import { GetFilterUrl, showToastNotification } from '../../utility/helper';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import {
  ICategoryData,
  ICategoryResponse,
} from '@/interfaces/categoryResponse.interface';
import ImagePreview from '@/components/common/media/ImagePreview';
import { PageInfo } from '@/interfaces/common.interface';
import { callApi, ThrowError } from '@/api/callApi';
import DragDropList from '@/components/common/commonTable/DragDropList';
import Pagination from '@/components/common/Pagination';
import Card from '@/components/UI/Card';

interface StateProps {
  categories: ICategoryData[];
  loading: boolean;
  saving: boolean;
  pageInfo: PageInfo;
}

const CategoriesManager = () => {
  const [states, setStates] = useState<StateProps>({
    loading: false,
    categories: [],
    saving: false,
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
  });

  const handleChangeState = (newState: Partial<StateProps>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const getCategories = async () => {
    handleChangeState({ loading: true });
    try {
      const filterQuery = {
        sort: 'order_index',
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
      };
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}?${query ? query : ''}`;
      const response = await callApi<ICategoryResponse>(PATH, {
        method: 'GET',
      });
      const normalized = response.data
        .map((c, i: number) => ({
          ...c,
          orderIndex: c.orderIndex ?? i,
        }))
        .sort((a, b) => a.orderIndex - b.orderIndex);

      handleChangeState({
        categories: normalized,
        pageInfo: response.pageInfo,
      });
    } catch (error) {
      ThrowError(error);
    } finally {
      handleChangeState({ loading: false });
    }
  };

  useEffect(() => {
    getCategories();
  }, [states.pageInfo.page, states.pageInfo.limit]);

  const setCategories = (items: ICategoryData[]) => {
    handleChangeState({
      categories: items.map((item, index) => ({
        ...item,
        orderIndex: index + 1,
      })),
    });
  };

  const saveOrder = async () => {
    handleChangeState({ saving: true });
    try {
      const payload = states.categories.map((c) => ({
        id: c.id,
        order: c.orderIndex,
      }));
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.categories}/reorder/items`;
      await callApi(PATH, {
        method: 'PATCH',
        data: payload,
      });

      showToastNotification('success', 'Category order saved successfully');
    } catch (error) {
      ThrowError(error);
    } finally {
      handleChangeState({ saving: false });
    }
  };

  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleChangeState({
      pageInfo: {
        ...states.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  return (
    <Layout>
      <Card>
        <DragDropList
          title="Primary Categories"
          data={states.categories}
          isLoading={states.loading}
          getId={(item) => item.id}
          renderItem={(item) => (
            <div className="flex items-center gap-3 w-full">
              <ImagePreview
                src={`${API_URL_PRODUCT_SERVICE}/${item?.media?.[0]?.url}`}
                text={item.name}
              />
              <span className="ml-auto text-muted">#{item.orderIndex}</span>
            </div>
          )}
          onReorder={setCategories}
          onSave={saveOrder}
          saving={states.saving}
        />
        <Pagination
          isLoading={states.loading}
          paginationData={states.pageInfo}
          onPageChange={(page) => handleChangePage(page)}
          onPageSizeChange={handleChangePageSize}
        />
      </Card>
    </Layout>
  );
};

export default CategoriesManager;
